import Button from "@marginly/ui/components/button";
import { Superfield } from "@marginly/ui/components/input";
import { FieldInnerContainer } from "@marginly/ui/components/input";
import { useFocus } from "@marginly/ui/components/input/use-focus";
import Label from "@marginly/ui/components/label";
import Thumbnail from "@marginly/ui/components/thumbnail";
import T from "@marginly/ui/components/typography";
import SwapIcon from "@marginly/ui/icons/swap";
import cn from "classnames";
import NextLink from "next/link";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";

import { getFarmTokenSymbol, getTokenSymbol } from "@/app/util";
import { LendPoolInfo } from "@/components/common/lend-pool-info";
import SwitchNetworkOr from "@/components/common/switch-network-or";
import { TokenIconAdaptive } from "@/components/common/token/token-icon/styled";
import ConnectWalletModal from "@/components/navigation/connect-wallet";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import {
  ModalBody,
  ModalFarm,
  ModalLayout,
} from "@/external/components/modal.styled";
import { useClickOutside } from "@/hooks/common";
import ArrowRightIcon from "@/icons/arrow-right-icon";
import { Bonus } from "@/types/common";
import { isValidInputAmount } from "@/util/check";
import { daysDisplay } from "@/util/days-display";
import { formatNum } from "@/util/format";
import { formatCoin } from "@/util/format-coin";
import { formatPercent } from "@/util/formatters";

import Portal from "../../common/portal";
import Space from "../../common/space";
import { Noop } from "../../util";
import {
  AssetsRow,
  DetailsButton,
  DropdownBody,
  DropdownLayer,
  DropdownWrapper,
  FarmModalInfo,
  FarmModalTitle,
  FarmRow,
  HighImpactWarning,
  PointsBlock,
} from "../styled";
import { leverageByRiskLevel, RiskLevel, useFarm } from "../use-farm";
import { getFarmingAlertText } from "../util";
import { AssetItem } from "./asset-item";
import PoweredImg from "./powered.png";
import {
  BadgeRow,
  BonusRow,
  IconWrapper,
  InputWrapper,
  RiskDescription,
  RiskSection,
  RiskSubtitle,
  TabItem,
  Tabs,
  TokenImage as TokenImageHeader,
  TooltipLink,
  TradeLink,
} from "./styled";

const RISK_LINK =
  "https://docs.marginly.com/risk-management/liquidations-and-deleveraging";

const labelByRiskLevel: Record<RiskLevel, string> = {
  low: "Low",
  medium: "Mid",
  hight: "High",
};

export const FarmModal = ({
  onClose,
  symbol,
  days,
  maturityDate,
  poolAddress,
  chainName,
}: {
  onClose: () => void;
  symbol?: string;
  days: number | null;
  maturityDate?: string;
  chainName: string;
  poolAddress: `0x${string}`;
}) => {
  useEffect(() => {
    return () => {
      document.body.style.overflow = "visible";
    };
  }, []);

  const [isConnectModal, setIsConnectModal] = useState(false);
  const [isDetails, setIsDetails] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const fromRef = useRef<HTMLLabelElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => {
    setIsCollapsed(true);
  });

  useClickOutside(fromRef, blur);

  const account = useAccount();

  const { focused, onClick, ref } = useFocus();

  const farm = useFarm({
    poolAddress,
    chainName,
    days,
    maturityDate,
    onClose,
  });

  const {
    apyByRiskLevel,
    base,
    basePrice,
    collateral,
    debt,
    debtAtMaturity,
    depositAmountInQuote,
    earnApy,
    flowModalStep,
    handleCloseClick,
    handleFarmClick,
    handleInputChange,
    handleMaxClick,
    handleMinClick,
    handleSelectToken,
    handleSubmit,
    inputAmount,
    isButtonDisabled,
    isPriceImpactHigh,
    leverage,
    min,
    max,
    netPositionAtMaturity,
    payApr,
    priceImpact,
    priceWithImpact,
    quote,
    riskLevel,
    selectedToken,
    sparksPerQuotePerDay,
    pending,
    notEnoughFunds,
    notEnoughToOpenPosition,
    tokens,
    setRiskLevel,
    liquidationPrice,
    isPendle,
    liquidationDate,
    receiveSum,
  } = farm;

  const activeSymbol = selectedToken?.symbol;

  if (flowModalStep) {
    return (
      <Portal>
        <ModalFarm className="farm">
          <ModalLayout onClick={handleCloseClick} />
          <Button onClick={handleCloseClick} className="close">
            <img src="/images/close.svg" alt="" />
          </Button>
          <ModalBody className="step">
            {flowModalStep === "approveSwap" ? (
              <>
                <Thumbnail xl accent className="thumbnail">
                  <img src="/images/collateral.svg" alt="" />
                </Thumbnail>
                <Space height={40} heightMobile={40} />

                <T headerM>Approve the spending in your wallet</T>
                <Space height={8} heightMobile={8} />
                <T body>
                  To allow smart contract to swap your{" "}
                  {getFarmTokenSymbol(selectedToken?.symbol)} for {base?.symbol}
                </T>
                <Space height={48} heightMobile={48} />
                <Button
                  lg
                  className="primary"
                  pending={pending}
                  onClick={handleSubmit}
                >
                  <T action>
                    Approve {inputAmount}{" "}
                    {getFarmTokenSymbol(selectedToken?.symbol)}
                  </T>
                </Button>
                <Space height={16} heightMobile={8} />
                <Button lg secondary onClick={handleCloseClick}>
                  <T action>Change Amount</T>
                </Button>
              </>
            ) : flowModalStep === "makeSwap" ? (
              <>
                <Thumbnail xl accent className="thumbnail">
                  <SwapIcon />
                </Thumbnail>
                <Space height={40} heightMobile={40} />

                <T headerM>Confirm the exchange in&nbsp;your wallet first</T>
                <Space height={8} heightMobile={8} />
                <T body>
                  Your {getFarmTokenSymbol(selectedToken?.symbol)} will be
                  swapped for {base?.symbol}
                </T>
                <Space height={48} heightMobile={48} />
                <Button
                  lg
                  className="primary"
                  pending={pending}
                  onClick={handleSubmit}
                >
                  <T action>Continue</T>
                </Button>
              </>
            ) : flowModalStep === "approveFarm" ? (
              <>
                <Thumbnail xl accent className="thumbnail">
                  <img src="/images/collateral.svg" alt="" />
                </Thumbnail>
                <Space height={40} heightMobile={40} />

                <T headerM>Approve the spending in your wallet</T>
                <Space height={8} heightMobile={8} />
                <T body>To allow smart contract to spend your {base?.symbol}</T>
                <Space height={48} heightMobile={48} />
                <Button
                  lg
                  className="primary"
                  pending={pending}
                  onClick={handleSubmit}
                >
                  <T action>
                    Approve {formatNum(inputAmount)} {base?.symbol}
                  </T>
                </Button>
                <Space height={16} heightMobile={8} />
                <Button lg secondary onClick={handleCloseClick}>
                  <T action>Change Amount</T>
                </Button>
              </>
            ) : flowModalStep === "makeFarm" ? (
              <>
                <Thumbnail xl accent className="thumbnail">
                  <img src="/images/deposit.svg" alt="" />
                </Thumbnail>
                <Space height={40} heightMobile={40} />

                <T headerM>Confirm to open farming in&nbsp;your wallet</T>
                <Space height={8} heightMobile={8} />
                <T body>Now you can farm {base?.symbol}</T>
                <Space height={48} heightMobile={48} />
                <Button
                  lg
                  className="primary"
                  pending={pending}
                  onClick={handleSubmit}
                >
                  <T action>Farm</T>
                </Button>
              </>
            ) : (
              <></>
            )}
          </ModalBody>
        </ModalFarm>
      </Portal>
    );
  }

  const renderSubmitButtonLabel = () => {
    if (!maturityDate || !collateral.toNumber()) {
      return "Farm";
    }
    return `Receive ~${formatNum(receiveSum)} ${getFarmTokenSymbol(
      selectedToken?.symbol,
    )} on ${new Date(maturityDate).toLocaleDateString("en-US", {
      weekday: undefined,
      day: "numeric",
      month: "short",
      year: undefined,
    })}`;
  };

  return (
    <Portal>
      <ModalFarm className="farm">
        <ModalLayout onClick={onClose} />
        <Button onClick={onClose} className="close">
          <img src="/images/close.svg" alt="" />
        </Button>

        <ModalBody>
          <div>
            <Button onClick={onClose} className="close close--mobile">
              <img src="/images/back-button.svg" alt="" />
            </Button>

            <FarmModalTitle>
              <div>
                <T headerL>{getFarmTokenSymbol(symbol)} farm</T>

                <T secondary body>
                  Harvest{" "}
                  {maturityDate && (
                    <span>yield in {daysDisplay(days || 0)}</span>
                  )}
                  .
                </T>
              </div>
              <IconWrapper className={isPendle ? "pendle" : ""}>
                <TokenImageHeader token={base?.symbol} />
                {isPendle && (
                  <img src={PoweredImg.src} alt="" className="powered" />
                )}
              </IconWrapper>
            </FarmModalTitle>
            <Space height={0} heightMobile={2} />

            <Space height={24} heightMobile={24} />

            <InputWrapper style={{ position: "relative" }}>
              <Superfield
                ref={ref}
                focused={focused}
                tabIndex={1}
                postfix={getFarmTokenSymbol(selectedToken?.symbol)}
                title="Deposit now"
                value={inputAmount}
                onClick={onClick}
                placeholder="Enter amount"
                onChange={handleInputChange}
                controls
                error={notEnoughFunds || notEnoughToOpenPosition}
              >
                {account.isConnected && focused ? (
                  <FieldInnerContainer suggestion style={{ zIndex: 4 }}>
                    {min && (
                      <Label badge onClick={handleMinClick}>
                        <div>Min&nbsp;{formatCoin(+min)}&nbsp;</div>
                      </Label>
                    )}
                    {max && !max.eq(0) && (
                      <Label badge onClick={handleMaxClick}>
                        <div>Max&nbsp;{formatCoin(+max)}&nbsp;</div>
                      </Label>
                    )}
                  </FieldInnerContainer>
                ) : (
                  <Noop />
                )}
              </Superfield>

              <DropdownWrapper>
                <Button
                  className={cn({
                    disabled: !isCollapsed,
                  })}
                  elevated
                  md
                  icon={
                    activeSymbol && <TokenIconAdaptive token={activeSymbol} />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCollapsed(!isCollapsed);
                  }}
                />
                {!isCollapsed ? (
                  <div>
                    <Portal>
                      <DropdownLayer
                        onClick={() => setIsCollapsed((v) => !v)}
                      />

                      <DropdownBody className="body-portal">
                        <div>
                          <T headerS className="header">
                            Choose an asset
                          </T>
                          <Space height={6} heightMobile={24} />

                          <AssetsRow>
                            {tokens?.map((token, i) => (
                              <AssetItem
                                key={token.symbol}
                                onClick={() => {
                                  setIsCollapsed(true);
                                  handleSelectToken(i);
                                }}
                                tokenAddress={token.address}
                                isSelected={
                                  selectedToken?.address === token.address
                                }
                                symbol={token.symbol}
                              />
                            ))}
                          </AssetsRow>
                        </div>
                      </DropdownBody>
                    </Portal>

                    <DropdownBody className="non-portal" ref={dropdownRef}>
                      <div>
                        <AssetsRow>
                          {tokens?.map((token, i) => (
                            <AssetItem
                              key={token.symbol}
                              onClick={() => {
                                setIsCollapsed(true);
                                handleSelectToken(i);
                              }}
                              tokenAddress={token.address}
                              isSelected={
                                selectedToken?.address === token.address
                              }
                              symbol={token.symbol}
                            />
                          ))}
                        </AssetsRow>
                      </div>
                    </DropdownBody>
                  </div>
                ) : (
                  <Noop />
                )}
              </DropdownWrapper>
            </InputWrapper>
            <Space height={16} heightMobile={16} />
            <RiskSection>
              <RiskDescription>
                <T headerS>APY</T>
                <RiskSubtitle>
                  <T body className="risk-title" secondary>
                    {labelByRiskLevel[riskLevel]} risk
                  </T>
                  <InfoTooltip
                    content={
                      <>
                        This is a leverage yield farming strategy. Higher
                        leverage = higher yield, but also higher risk of loss
                        via liquidation.{" "}
                        <TooltipLink
                          href={RISK_LINK}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Learn more
                        </TooltipLink>
                      </>
                    }
                  />
                </RiskSubtitle>
              </RiskDescription>

              <Tabs>
                {Object.keys(leverageByRiskLevel).map((level_) => {
                  const level = level_ as RiskLevel;
                  const isActive = riskLevel === level;
                  return (
                    <TabItem
                      key={level}
                      className={isActive ? "active" : ""}
                      onClick={() => setRiskLevel(level)}
                    >
                      +{" "}
                      {formatPercent(apyByRiskLevel[level] / 100, {
                        maximumFractionDigits: 1,
                      })}
                    </TabItem>
                  );
                })}
              </Tabs>
            </RiskSection>
            <Space height={32} heightMobile={32} />
            <FarmRow>
              <T headerS>Details</T>
              <DetailsButton>
                <T action secondary onClick={() => setIsDetails(!isDetails)}>
                  {isDetails ? `Hide` : `Show all`}
                </T>
              </DetailsButton>
            </FarmRow>
            <Space height={2} heightMobile={2} />
            <FarmRow>
              <T body>Bonuses</T>
              <BonusRow>
                {sparksPerQuotePerDay && (
                  <PointsBlock>
                    <img src="/images/marginly-point-on-light.svg" alt="" />
                    <T caption>
                      {sparksPerQuotePerDay && isValidInputAmount(inputAmount)
                        ? `${formatNum(
                            ((+inputAmount * sparksPerQuotePerDay) / 10) *
                              leverage,
                            {
                              dp: 0,
                            },
                          )} per day`
                        : "Sparks"}
                    </T>
                  </PointsBlock>
                )}
              </BonusRow>
            </FarmRow>

            {isDetails ? (
              <div>
                <LendPoolInfo
                  apr={apyByRiskLevel[riskLevel]}
                  baseSymbol={base?.symbol}
                  quoteSymbol={quote?.symbol}
                  poolAddress={poolAddress}
                />
                <Space height={8} heightMobile={8} />
                {days === null ? null : (
                  <FarmRow>
                    <T body className="info-row">
                      Maturity{" "}
                      <InfoTooltip
                        content={
                          <>
                            Pendle&apos;s Principal Tokens (PTs) become
                            redeemable for underlying asset at 1 to 1 ratio.
                            Pendle pools stop swapping PTs for underlying asset
                            and work in PTs redeem for underlying mode.
                          </>
                        }
                      />
                    </T>
                    <T body>{daysDisplay(days)}</T>
                  </FarmRow>
                )}
                <FarmRow>
                  <T body>{getFarmTokenSymbol(symbol)} price</T>
                  <T body>
                    {formatNum(basePrice)} {quote?.symbol}
                  </T>
                </FarmRow>
                <FarmRow>
                  <T body>Liquidation</T>
                  {liquidationPrice ? (
                    <T body>
                      at {formatNum(liquidationPrice)} {quote?.symbol}
                    </T>
                  ) : null}
                </FarmRow>
                {priceImpact && (
                  <FarmRow>
                    <T body className="info-row">
                      Price impact
                      <InfoTooltip
                        content={
                          <>
                            The impact your transaction has on the market price
                            of this pool. Price will change to ~
                            {formatNum(priceWithImpact)} {quote?.symbol}{" "}
                          </>
                        }
                      />
                    </T>
                    <T body className={isPriceImpactHigh ? "negative" : ""}>
                      ~{formatPercent(Math.abs(priceImpact))}{" "}
                    </T>
                  </FarmRow>
                )}

                <FarmRow>
                  <div>
                    <T body>Collateral</T>
                    {earnApy ? (
                      <T secondary caption>
                        You earn {formatPercent(earnApy)}
                      </T>
                    ) : null}
                  </div>
                  {collateral.toNumber() ? (
                    <div>
                      <T body>
                        {formatNum(collateral)} {getFarmTokenSymbol(symbol)}
                      </T>
                      {maturityDate && (
                        <T secondary caption>
                          {formatNum(collateral)} {quote?.symbol} at maturity
                        </T>
                      )}
                    </div>
                  ) : null}
                </FarmRow>

                <FarmRow>
                  <div>
                    <T body>Debt</T>
                    {payApr && (
                      <T secondary caption>
                        You pay {formatPercent(payApr)}
                      </T>
                    )}
                  </div>
                  {debt.toNumber() ? (
                    <div>
                      <T body>
                        {formatNum(debt)} {quote?.symbol}
                      </T>
                      {maturityDate ? (
                        <T secondary caption>
                          {formatNum(debtAtMaturity)} {quote?.symbol} at
                          maturity
                        </T>
                      ) : null}
                    </div>
                  ) : null}
                </FarmRow>

                {maturityDate && (
                  <FarmRow>
                    <T body>Expected profit</T>
                    {netPositionAtMaturity ? (
                      <T body>
                        {formatNum(
                          netPositionAtMaturity -
                            depositAmountInQuote.toNumber(),
                        )}{" "}
                        {quote?.symbol}
                      </T>
                    ) : null}
                  </FarmRow>
                )}
                <FarmRow>
                  <T body>Leverage {leverage}&times;</T>
                  <Link href={`/${chainName}/trade/${poolAddress}`}>
                    <TradeLink>
                      <T body secondary>
                        Trade with custom
                      </T>
                      <ArrowRightIcon />
                    </TradeLink>
                  </Link>
                </FarmRow>
              </div>
            ) : (
              <Noop />
            )}

            {isPriceImpactHigh ? (
              <>
                <Space height={24} heightMobile={24} />
                <HighImpactWarning>
                  <img src="/images/alert.svg" alt="" />
                  <T body>
                    Price impact is too high.You can experience a significant
                    loss.
                  </T>
                </HighImpactWarning>
              </>
            ) : (
              <Noop />
            )}
            <Space height={32} heightMobile={32} />
            <SwitchNetworkOr chainName={chainName}>
              <Button
                lg
                className={cn(
                  {
                    disabled: isButtonDisabled && account.address,
                  },
                  "btn--action",
                )}
                onClick={() => {
                  if (!account.address) {
                    setIsConnectModal(true);
                    return;
                  }
                  handleFarmClick();
                }}
              >
                {renderSubmitButtonLabel()}
              </Button>
            </SwitchNetworkOr>
            {liquidationDate && (
              <>
                <Space height={12} heightMobile={12} />
                <FarmModalInfo>
                  <T caption secondary>
                    {getFarmingAlertText(liquidationDate)}
                  </T>
                </FarmModalInfo>
              </>
            )}
          </div>
        </ModalBody>

        {isConnectModal ? (
          <ConnectWalletModal
            onHide={() => {
              setIsConnectModal(false);
            }}
          />
        ) : (
          <Noop />
        )}
      </ModalFarm>
    </Portal>
  );
};
